import {Box, FormControlLabel, Typography} from '@mui/material';
import {t} from 'i18next';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrTreeItemSelector, {
  SelectedItemsData,
  TabItem,
  TabGroup,
} from '../../common/arr-tree-item-selector/ArrTreeItemSelector';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {useDispatch, useSelector} from 'react-redux';
import {setSaveBatchPrint} from '../../../redux/slice/batchPrintSlice';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrTextArea from '../../common/arr-text-area/ArrTextArea';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrDatePicker from '../../common/arr-date-picker/ArrDatePicker';
import './SlipSheetAndCoverPage.scss';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {useGetSlipSheets} from '../../../hooks/documentPrinting/useDocumentPrinting';
import {formatDateCommon} from '../../../utils/utils';

type SlipTreeItem = TreeViewBaseItem<ExtendedTreeItemProps>;

type SlipSheetsResponse = {
  metadataList: any[];
  folderList: any[];
  tagList: any[];
  redactionTagList: any[];
  commentsList: any[];
  assignmentList: any[];
};

type FlatNode = {
  id: string | number;
  text: string;
  parent?: string | number;
  state?: {disabled?: boolean};
};

const getSlipSheetDataType = (treeId: string): number => {
  const typeMap: {[key: string]: number} = {
    metadata: 6, //  (metadata)
    workProductFolders: 3, // (all folders)
    workProductTags: 5, // (tags)
    workProductRedactionTags: 4, // (redaction)
    workProductComments: 1, // (All comments)
    workProductAssignments: 0, //  (Assignments)
  };
  return typeMap[treeId] ?? 0;
};

// Reverse mapping: convert slipSheetDataType back to treeId
const getTreIdFromDataType = (dataType: number): string => {
  const reverseMap: {[key: number]: string} = {
    6: 'metadata',
    3: 'workProductFolders',
    5: 'workProductTags',
    4: 'workProductRedactionTags',
    1: 'workProductComments',
    0: 'workProductAssignments',
  };
  return reverseMap[dataType] ?? 'metadata';
};

// Build slip sheet details with correct type mapping and field lookup
const buildSlipSheetDetails = (
  items: SlipTreeItem[],
  getSlipSheets: SlipSheetsResponse | undefined
) => {
  if (!items || !getSlipSheets) {
    return [];
  }

  return items.map((item) => {
    const treeId = item.treeId || '';
    let fieldID = 0;
    let fieldName = '';

    // For metadata, use fieldID and fieldLabel from metadataList
    if (treeId === 'metadata' && getSlipSheets.metadataList) {
      const metaField = getSlipSheets.metadataList.find(
        (field: any) => field.fieldID.toString() === item.id.toString()
      );
      if (metaField) {
        fieldID = metaField.fieldID;
        fieldName = metaField.fieldLabel;
      }
    } else {
      // For other types (folders, tags, redaction, comments, assignments), use id as fieldID and label as fieldName (text)
      fieldID = Number.isNaN(Number(item.id)) ? 0 : Number(item.id);
      fieldName = item.label || '';
    }

    return {
      slipSheetDataType: getSlipSheetDataType(treeId),
      value: fieldID.toString(),
      fieldName,
      fieldID,
    };
  });
};

const buildTreeFromFlat = <T extends FlatNode>(
  items: T[],
  treeId: string,
  options?: {
    getFileType?: (item: T) => string | undefined;
    markSkipCheckbox?: (item: T) => boolean;
  }
): SlipTreeItem[] => {
  if (!items || items.length === 0) {
    return [];
  }

  const nodeMap = new Map<string, SlipTreeItem>();

  items.forEach((item) => {
    const nodeId = item.id.toString();
    const skipCheckbox = options?.markSkipCheckbox?.(item) ?? false;
    const fileType = options?.getFileType?.(item);
    nodeMap.set(nodeId, {
      id: nodeId,
      label: item.text,
      treeId,
      fileType: skipCheckbox ? fileType : undefined,
      disabled: item.state?.disabled ?? false,
      skipParent: skipCheckbox,
      children: [],
    });
  });

  const roots: SlipTreeItem[] = [];

  items.forEach((item) => {
    const nodeId = item.id.toString();
    const parentId = item.parent?.toString();
    const node = nodeMap.get(nodeId);

    if (!node) {
      return;
    }

    if (parentId && parentId !== '#') {
      const parent = nodeMap.get(parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  return roots;
};

// Memoized tree selector to prevent re-renders when parent state changes
const MemoizedTreeSelector = React.memo<{
  restorationKey: number;
  tabs: TabItem[];
  onSelectedItemsChange: (data: SelectedItemsData) => void;
}>(({restorationKey, tabs, onSelectedItemsChange}) => (
  <ArrTreeItemSelector
    key={`tree-restoration-${restorationKey}`}
    tabs={tabs}
    onSelectedItemsChange={onSelectedItemsChange}
    isPreExpanded={true}
    disableSelectedInAvailable={true}
    showPanelTitle={false}
    parentDisabled={false}
    disabled={false}
    enableGroupedByTab={false}
  />
));

const focusSectionContainer = (container: HTMLElement | null) => {
  if (!container) {
    return;
  }

  window.setTimeout(() => {
    container.focus();
  }, 0);
};

const handleTooltipTriggerKeyDown = (
  e: React.KeyboardEvent<HTMLButtonElement>
) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.currentTarget.click();
  }
};

type AccessibleTooltipTriggerProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    iconClassName?: string;
  };

const AccessibleTooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  AccessibleTooltipTriggerProps
>(
  (
    {label, iconClassName = 'info-icon', onKeyDown, className, ...rest},
    ref
  ) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      handleTooltipTriggerKeyDown(e);
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        aria-label={label}
        onKeyDown={handleKeyDown}
        className={`tooltip-trigger ${className ?? ''}`.trim()}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          margin: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <InfoIcon
          fontSize="small"
          className={iconClassName}
          aria-hidden="true"
          focusable="false"
        />
      </button>
    );
  }
);

AccessibleTooltipTrigger.displayName = 'AccessibleTooltipTrigger';

const SlipSheetAndCoverPage = React.forwardRef<any, any>((props, ref) => {
  const dispatch = useDispatch();

  // Only subscribe to printIssueSettingCode (needed for dropdown enable/disable)
  const printIssueSettingCode = useSelector(
    (state: any) => state?.batchPrint?.basisForPrinting?.printIssueSettingCode
  );

  // Get initial values from Redux ONCE with a selector that always returns true for equality
  // This prevents re-renders when batchPrintDTO changes
  const batchPrintDTO = useSelector(
    (state: any) => state?.batchPrint?.saveBatchPrint?.batchPrintDTO,
    () => true // Always return true = never re-render for this selector
  );

  // Separate selector for slipSheetDetails to track changes
  const savedSlipSheetDetails = useSelector(
    (state: any) =>
      state?.batchPrint?.saveBatchPrint?.batchPrintDTO?.slipSheetDetails || []
  );

  // Enable dropdown if "Prior Productions" radio button is selected
  const isDropdownEnabled = printIssueSettingCode === 'IGNOREPRINT';

  const slipSheetTypeOptions = useMemo(
    () => [
      {value: t('Create New Slip Sheets'), key: 'NewSlipSheets'},
      {
        value: t('Use Slip Sheets from Prior Productions'),
        key: 'PriorProductionSlipSheets',
      },
    ],
    []
  );

  const [slipSheetType, setSlipSheetType] = useState<{
    key: string;
    value: string;
  }>(() => {
    // Initialize from Redux
    const isNewFromRedux = batchPrintDTO?.isNewSlipSheet;
    if (isNewFromRedux) {
      return {
        key: 'NewSlipSheets',
        value: t('Create New Slip Sheets'),
      };
    }
    return {
      key: 'PriorProductionSlipSheets',
      value: t('Use Slip Sheets from Prior Productions'),
    };
  });

  // Use lazy initialization - only reads batchPrintDTO once
  const [includeTableOfContent, setIncludeTableOfContent] = useState(
    () => !!batchPrintDTO?.includeTableOfContent
  );
  const [displayTOCNumberInSeq, setDisplayTOCNumberInSeq] = useState(
    () => !!batchPrintDTO?.displayTOCNumberInSeq
  );
  const [includeSlipSheet, setIncludeSlipSheet] = useState(
    () => !!batchPrintDTO?.includeSlipSheet
  );
  const [includeCoverIntroPage, setIncludeCoverIntroPage] = useState(
    () => !!batchPrintDTO?.includeCoverIntroPage
  );
  const [selectedSlipItems, setSelectedSlipItems] = useState<SlipTreeItem[]>(
    []
  );

  // Snapshot used only to seed ArrTreeItemSelector on restore (avoids ref timing issues)
  const [treePreSelectedItems, setTreePreSelectedItems] = useState<
    SlipTreeItem[]
  >([]);

  // Track restoration to create stable key - only changes when restoring from Redux
  const [restorationKey, setRestorationKey] = useState(0);

  // Track if Redux restoration has completed (prevents auto-save from clearing saved data)
  const [isRestoredFromRedux, setIsRestoredFromRedux] = useState(false);

  // Track if component is initialized to prevent premature saves
  const [isInitialized, setIsInitialized] = useState(false);

  // Debounce autosave to Redux to avoid heavy work on every small change
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cover page form fields state - Lazy initialization from Redux
  const [recipientName, setRecipientName] = useState(
    () => batchPrintDTO?.coverSheetRecipientName || ''
  );
  const [senderName, setSenderName] = useState(
    () => batchPrintDTO?.coverSheetSenderName || ''
  );
  const [ccName, setCcName] = useState(
    () => batchPrintDTO?.coverSheetCarbonCopyName || ''
  );
  const [title, setTitle] = useState(
    () => batchPrintDTO?.coverSheetTitle || ''
  );
  const [description, setDescription] = useState(
    () => batchPrintDTO?.coverSheetDescription || ''
  );
  const [coverDate, setCoverDate] = useState(
    () => batchPrintDTO?.coverSheetDate || ''
  );

  const {data: getSlipSheets} = useGetSlipSheets() as {
    data: SlipSheetsResponse | undefined;
  };

  const tocCheckboxRowRef = useRef<HTMLDivElement | null>(null);
  const slipSheetTypeSectionRef = useRef<HTMLDivElement | null>(null);
  const slipSheetSelectorRef = useRef<HTMLDivElement | null>(null);
  const coverPageFormRef = useRef<HTMLDivElement | null>(null);
  const coverDatePickerWrapperRef = useRef<HTMLDivElement | null>(null);

  const previousIncludeTableOfContentRef = useRef(includeTableOfContent);
  const previousIncludeSlipSheetRef = useRef(includeSlipSheet);
  const previousIncludeCoverIntroPageRef = useRef(includeCoverIntroPage);
  const previousSlipSheetTypeRef = useRef(slipSheetType.key);
  const selectedSlipItemsRef = useRef(selectedSlipItems);

  useEffect(() => {
    selectedSlipItemsRef.current = selectedSlipItems;
  }, [selectedSlipItems]);

  useEffect(() => {
    if (getSlipSheets && isRestoredFromRedux) {
      // Small delay to ensure tree has applied restored selections
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [getSlipSheets, isRestoredFromRedux]);

  // Sync Redux-saved selections into state whenever data loads or Redux changes
  // Use a ref to track previous values and prevent redundant updates
  const hasRestoredFromReduxRef = useRef(false);

  const selectedPanelTitleFocusRequestedRef = useRef(false);
  const previousSelectedSlipItemsCountRef = useRef(0);

  useEffect(() => {
    if (!getSlipSheets) {
      return;
    }

    // Only perform Redux -> local restoration once per mount.
    if (hasRestoredFromReduxRef.current) {
      return;
    }

    // If no saved details on first load, keep selections empty.
    if (
      !Array.isArray(savedSlipSheetDetails) ||
      savedSlipSheetDetails.length === 0
    ) {
      setSelectedSlipItems([]);
      setTreePreSelectedItems([]);
      hasRestoredFromReduxRef.current = true;
      setIsRestoredFromRedux(true);
      return;
    }

    // Restore selections from Redux (initial load)
    const restoredItems = savedSlipSheetDetails.map((detail: any) => ({
      id: detail.fieldID?.toString() || '',
      label: detail.fieldName || '',
      treeId: getTreIdFromDataType(detail.slipSheetDataType),
    }));

    setTreePreSelectedItems(restoredItems);
    setSelectedSlipItems(restoredItems);
    selectedSlipItemsRef.current = restoredItems;
    // Increment restoration key to force tree remount with restored selections
    setRestorationKey((prev) => prev + 1);
    hasRestoredFromReduxRef.current = true;
    setIsRestoredFromRedux(true);
  }, [getSlipSheets, savedSlipSheetDetails]);

  // Function to save all data to Redux (called on Next/Back button click)
  const saveSlipSheetPageData = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    const builtDetails =
      includeSlipSheet && getSlipSheets
        ? buildSlipSheetDetails(selectedSlipItemsRef.current, getSlipSheets)
        : [];
    dispatch(
      setSaveBatchPrint({
        includeTableOfContent,
        displayTOCNumberInSeq: includeTableOfContent
          ? displayTOCNumberInSeq
          : true,
        includeSlipSheet,
        isNewSlipSheet: slipSheetType.key === 'NewSlipSheets',
        slipSheetDetails: builtDetails,
        includeCoverIntroPage,
        coverSheetRecipientName: includeCoverIntroPage ? recipientName : '',
        coverSheetSenderName: includeCoverIntroPage ? senderName : '',
        coverSheetCarbonCopyName: includeCoverIntroPage ? ccName : '',
        coverSheetTitle: includeCoverIntroPage ? title : '',
        coverSheetDescription: includeCoverIntroPage ? description : '',
        coverSheetDate: includeCoverIntroPage
          ? coverDate || formatDateCommon(new Date().toISOString())
          : formatDateCommon(new Date().toISOString()),
      })
    );
  }, [
    dispatch,
    includeTableOfContent,
    displayTOCNumberInSeq,
    includeSlipSheet,
    slipSheetType.key,
    getSlipSheets,
    includeCoverIntroPage,
    recipientName,
    senderName,
    ccName,
    title,
    description,
    coverDate,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      saveData: saveSlipSheetPageData,
    }),
    [saveSlipSheetPageData]
  );

  // Auto-save to Redux whenever form values change (only after initialization)
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    // Debounce saves so rapid updates (like drag/drop) don't cause jank
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      saveSlipSheetPageData();
      autoSaveTimeoutRef.current = null;
    }, 200);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [saveSlipSheetPageData, isInitialized]);

  useEffect(() => {
    const wasEnabled = previousIncludeTableOfContentRef.current;
    previousIncludeTableOfContentRef.current = includeTableOfContent;

    if (!wasEnabled && includeTableOfContent) {
      focusSectionContainer(tocCheckboxRowRef.current);
    }
  }, [includeTableOfContent]);

  useEffect(() => {
    const wasEnabled = previousIncludeSlipSheetRef.current;
    previousIncludeSlipSheetRef.current = includeSlipSheet;

    if (!wasEnabled && includeSlipSheet) {
      if (isDropdownEnabled) {
        focusSectionContainer(slipSheetTypeSectionRef.current);
      } else {
        focusSectionContainer(slipSheetSelectorRef.current);
      }
    }
  }, [includeSlipSheet, isDropdownEnabled]);

  useEffect(() => {
    const previousType = previousSlipSheetTypeRef.current;
    previousSlipSheetTypeRef.current = slipSheetType.key;

    if (
      includeSlipSheet &&
      isDropdownEnabled &&
      previousType !== slipSheetType.key &&
      slipSheetType.key === 'NewSlipSheets'
    ) {
      focusSectionContainer(slipSheetSelectorRef.current);
    }
  }, [includeSlipSheet, isDropdownEnabled, slipSheetType.key]);

  useEffect(() => {
    const wasEnabled = previousIncludeCoverIntroPageRef.current;
    previousIncludeCoverIntroPageRef.current = includeCoverIntroPage;

    if (!wasEnabled && includeCoverIntroPage) {
      focusSectionContainer(coverPageFormRef.current);
    }
  }, [includeCoverIntroPage]);

  const focusCoverDateCalendar = useCallback(() => {
    window.setTimeout(() => {
      const popper = document.querySelector(
        '[data-testid="popper-slip-sheet-and-cover-page-date-picker"]'
      );

      if (!(popper instanceof HTMLElement)) {
        return;
      }

      const focusTarget =
        (popper.querySelector(
          '.MuiPickersDay-root.Mui-selected'
        ) as HTMLElement | null) ||
        (popper.querySelector(
          '.MuiPickersDay-root.MuiPickersDay-today'
        ) as HTMLElement | null) ||
        (popper.querySelector(
          '.MuiPickersDay-root[tabindex="0"]'
        ) as HTMLElement | null) ||
        (popper.querySelector('.MuiPickersDay-root') as HTMLElement | null);

      focusTarget?.focus();
    }, 0);
  }, []);

  const openCoverDatePickerFromKeyboard = useCallback(() => {
    const wrapper = coverDatePickerWrapperRef.current;
    if (!wrapper) {
      return;
    }

    const calendarButton =
      (wrapper.querySelector(
        '[data-testid="arr-date-picker-calender-icon-icon-button"]'
      ) as HTMLElement | null) ||
      (wrapper.querySelector('button[aria-label]') as HTMLElement | null);

    if (!calendarButton) {
      return;
    }

    calendarButton.click();
    focusCoverDateCalendar();
  }, [focusCoverDateCalendar]);

  const handleCoverDatePickerKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const isCoverDateInput =
        target.getAttribute('data-testid') ===
          'slip-sheet-and-cover-page-date-picker-picker' ||
        !!target.closest(
          '[data-testid="input-date-slip-sheet-and-cover-page-date-picker"]'
        );

      if (!isCoverDateInput) {
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        openCoverDatePickerFromKeyboard();
      }
    },
    [openCoverDatePickerFromKeyboard]
  );

  const metadataTreeData: SlipTreeItem[] = useMemo(() => {
    if (!getSlipSheets || getSlipSheets.metadataList.length === 0) {
      return [];
    }

    // Map each metadata field directly to a tree item
    return getSlipSheets.metadataList.map((field) => ({
      id: field.fieldID.toString(),
      label: field.fieldLabel,
      treeId: 'metadata',
    }));
  }, [getSlipSheets]);

  // Memoize tree data separately from selections to avoid rebuilding on every selection change
  const workProductTreeData = useMemo(
    () => ({
      folders: buildTreeFromFlat(
        getSlipSheets?.folderList || [],
        'workProductFolders',
        {
          getFileType: (item) =>
            (item as any).type === 'folders' ? 'folder' : undefined,
          markSkipCheckbox: (item) => (item as any).type === 'folders',
        }
      ),
      tags: buildTreeFromFlat(getSlipSheets?.tagList || [], 'workProductTags', {
        getFileType: () => undefined,
      }),
      redactionTags: buildTreeFromFlat(
        getSlipSheets?.redactionTagList || [],
        'workProductRedactionTags',
        {
          getFileType: () => undefined,
        }
      ),
      comments: buildTreeFromFlat(
        getSlipSheets?.commentsList || [],
        'workProductComments'
      ),
      assignments: buildTreeFromFlat(
        getSlipSheets?.assignmentList || [],
        'workProductAssignments',
        {
          getFileType: (item) =>
            (item as any).type === 'folders' ? 'folder' : undefined,
          markSkipCheckbox: (item) => (item as any).type === 'folders',
        }
      ),
    }),
    [getSlipSheets]
  );

  // Memoize preSelectedItems by treeId - only for initial restoration, not live updates
  // This prevents re-renders during drag-and-drop operations
  const preSelectedItemsByTreeId = useMemo(() => {
    // Skip computation if tree won't be rendered
    if (
      !includeSlipSheet ||
      (isDropdownEnabled && slipSheetType.key !== 'NewSlipSheets')
    ) {
      return {
        metadata: [] as SlipTreeItem[],
        workProductFolders: [] as SlipTreeItem[],
        workProductTags: [] as SlipTreeItem[],
        workProductRedactionTags: [] as SlipTreeItem[],
        workProductComments: [] as SlipTreeItem[],
        workProductAssignments: [] as SlipTreeItem[],
      };
    }

    const grouped = {
      metadata: [] as SlipTreeItem[],
      workProductFolders: [] as SlipTreeItem[],
      workProductTags: [] as SlipTreeItem[],
      workProductRedactionTags: [] as SlipTreeItem[],
      workProductComments: [] as SlipTreeItem[],
      workProductAssignments: [] as SlipTreeItem[],
    };

    treePreSelectedItems.forEach((item) => {
      const treeId = item.treeId as keyof typeof grouped;
      if (grouped[treeId]) {
        grouped[treeId].push(item);
      }
    });

    return grouped;
  }, [
    restorationKey,
    treePreSelectedItems,
    includeSlipSheet,
    isDropdownEnabled,
    slipSheetType.key,
  ]);

  const slipSheetTabs: TabItem[] = useMemo(
    () => [
      {
        tabTitle: 'Metadata',
        groups: [
          {
            groupTitle: t('batchPrint.slipSheets'),
            treeData: metadataTreeData,
            treeId: 'metadata',
            preSelectedItems: preSelectedItemsByTreeId.metadata,
            treeItemIdField: 'id',
          } as TabGroup,
        ],
      },
      {
        tabTitle: 'WorkProduct',
        groups: [
          {
            groupTitle: 'Folders',
            treeData: workProductTreeData.folders,
            treeId: 'workProductFolders',
            showFileType: true,
            preSelectedItems: preSelectedItemsByTreeId.workProductFolders,
            treeItemIdField: 'id',
          } as TabGroup,
          {
            groupTitle: 'Tags',
            treeData: workProductTreeData.tags,
            treeId: 'workProductTags',
            showFileType: false,
            preSelectedItems: preSelectedItemsByTreeId.workProductTags,
            treeItemIdField: 'id',
          } as TabGroup,
          {
            groupTitle: 'Redaction Tags',
            treeData: workProductTreeData.redactionTags,
            treeId: 'workProductRedactionTags',
            showFileType: false,
            preSelectedItems: preSelectedItemsByTreeId.workProductRedactionTags,
            treeItemIdField: 'id',
          } as TabGroup,
          {
            groupTitle: 'Comments',
            treeData: workProductTreeData.comments,
            treeId: 'workProductComments',
            showFileType: false,
            preSelectedItems: preSelectedItemsByTreeId.workProductComments,
            treeItemIdField: 'id',
          } as TabGroup,
          {
            groupTitle: 'Assignments',
            treeData: workProductTreeData.assignments,
            treeId: 'workProductAssignments',
            showFileType: true,
            preSelectedItems: preSelectedItemsByTreeId.workProductAssignments,
            treeItemIdField: 'id',
          } as TabGroup,
        ],
      },
    ],
    [
      metadataTreeData,
      workProductTreeData,
      preSelectedItemsByTreeId,
      restorationKey,
    ]
  );

  // Optimized validation logic for enabling/disabling Next button
  const isSlipSheetStepValid = useMemo(() => {
    // When slip sheets are not enabled, step is always valid
    if (!includeSlipSheet) {
      return true;
    }

    // Only consider selected items that are enabled in the tree
    const hasEnabledSelection = selectedSlipItems.some(
      (item) => !item.disabled
    );

    // If dropdown is disabled (Viewable File is selected)
    if (!isDropdownEnabled) {
      // Require at least one enabled field selected from tree
      return hasEnabledSelection;
    }

    // If dropdown is enabled and "Create New Slip Sheets" is selected
    if (slipSheetType.key === 'NewSlipSheets') {
      // Require at least one enabled field selected from tree
      return hasEnabledSelection;
    }

    // If "Use Slip Sheets from Prior Productions" is selected, step is valid
    return slipSheetType.key === 'PriorProductionSlipSheets';
  }, [
    includeSlipSheet,
    isDropdownEnabled,
    slipSheetType.key,
    selectedSlipItems,
  ]);

  // Update Redux with validation state
  useEffect(() => {
    dispatch(
      setSaveBatchPrint({
        isSlipSheetStepValid,
      })
    );
  }, [dispatch, isSlipSheetStepValid]);

  // Event handlers for tree selection (memoized since passed to MemoizedTreeSelector)
  const handleSlipSheetSelection = useCallback((data: SelectedItemsData) => {
    // Update selected slip items immediately so drag order is preserved.
    setSelectedSlipItems(data.items || []);
  }, []);

  const enhanceSelectedFieldsFocus = useCallback(() => {
    const root = slipSheetSelectorRef.current;
    if (!root) {
      return;
    }

    const selectedLabels = root.querySelectorAll<HTMLElement>(
      '.selected-field-item .field-label'
    );

    selectedLabels.forEach((labelEl) => {
      labelEl.tabIndex = 0;

      const labelText = labelEl.textContent?.trim() ?? '';
      if (labelText) {
        labelEl.setAttribute('aria-label', `Selected source ${labelText}`);
      } else {
        labelEl.setAttribute('aria-label', 'Selected source');
      }
    });
  }, []);

  const enhanceTransferFocusBehavior = useCallback(() => {
    const root = slipSheetSelectorRef.current;
    if (!root) {
      return;
    }

    const transferButton = root.querySelector<HTMLElement>(
      '[data-testid="transfer-button"]'
    );

    const selectedPanelTitle = root.querySelector<HTMLElement>(
      '[data-testid="arr-tree-item-selector-selected-panel-title"]'
    );

    if (selectedPanelTitle) {
      selectedPanelTitle.tabIndex =
        selectedSlipItemsRef.current.length > 0 ? 0 : -1;
    }

    if (
      !transferButton ||
      (transferButton as any).dataset.focusEnhanced === 'true'
    ) {
      return;
    }

    const handleTransferButtonKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        selectedPanelTitleFocusRequestedRef.current = true;
      }
    };

    const handleTransferButtonClick = () => {
      selectedPanelTitleFocusRequestedRef.current = true;
    };

    transferButton.addEventListener('keydown', handleTransferButtonKeyDown);
    transferButton.addEventListener('click', handleTransferButtonClick);

    (transferButton as any).dataset.focusEnhanced = 'true';
    (transferButton as any).__cleanupTransferFocus = () => {
      transferButton.removeEventListener(
        'keydown',
        handleTransferButtonKeyDown
      );
      transferButton.removeEventListener('click', handleTransferButtonClick);
    };
  }, []);

  useEffect(() => {
    const shouldRenderSelector =
      includeSlipSheet &&
      (!isDropdownEnabled || slipSheetType.key === 'NewSlipSheets');

    if (!shouldRenderSelector) {
      return;
    }

    const root = slipSheetSelectorRef.current;
    if (!root) {
      return;
    }

    enhanceSelectedFieldsFocus();
    enhanceTransferFocusBehavior();

    const observer = new MutationObserver(() => {
      enhanceSelectedFieldsFocus();
      enhanceTransferFocusBehavior();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();

      const transferButton = root.querySelector<HTMLElement>(
        '[data-testid="transfer-button"]'
      ) as any;

      if (transferButton?.__cleanupTransferFocus) {
        transferButton.__cleanupTransferFocus();
        delete transferButton.__cleanupTransferFocus;
        delete transferButton.dataset.focusEnhanced;
      }
    };
  }, [
    includeSlipSheet,
    isDropdownEnabled,
    slipSheetType.key,
    restorationKey,
    selectedSlipItems.length,
    enhanceSelectedFieldsFocus,
    enhanceTransferFocusBehavior,
  ]);

  useEffect(() => {
    const currentCount = selectedSlipItems.length;
    const previousCount = previousSelectedSlipItemsCountRef.current;

    const itemsWereTransferred = currentCount > previousCount;

    previousSelectedSlipItemsCountRef.current = currentCount;

    const root = slipSheetSelectorRef.current;
    if (!root) {
      return;
    }

    const selectedPanelTitle = root.querySelector<HTMLElement>(
      '[data-testid="arr-tree-item-selector-selected-panel-title"]'
    );

    if (selectedPanelTitle) {
      selectedPanelTitle.tabIndex = currentCount > 0 ? 0 : -1;
    }

    if (
      itemsWereTransferred &&
      selectedPanelTitleFocusRequestedRef.current &&
      selectedPanelTitle
    ) {
      window.setTimeout(() => {
        selectedPanelTitle.focus();
      }, 0);
    }

    selectedPanelTitleFocusRequestedRef.current = false;
  }, [selectedSlipItems.length]);

  // Simple event handlers (no memoization needed)
  const handleSlipSheetTypeChange = (value: {key: string; value: string}) => {
    setSlipSheetType(value);
    // Clear selections when switching to Prior Productions (not needed for that option)
    if (value.key === 'PriorProductionSlipSheets') {
      setSelectedSlipItems([]);
    }
  };

  // const handleTableOfContentChange = (
  //   e: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   setIncludeTableOfContent(e.target.checked);
  // };
  const handleTableOfContentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isEnabled = e.target.checked;
    setIncludeTableOfContent(isEnabled);

    // When enabling Table of Contents, default the page-numbering
    // checkbox to checked; user can still uncheck it manually.
    if (isEnabled) {
      setDisplayTOCNumberInSeq(true);
    }
  };

  const handleDisplayTOCNumberChange = (checked: boolean) => {
    setDisplayTOCNumberInSeq(checked);
  };

  const handleIncludeSlipSheetChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIncludeSlipSheet(e.target.checked);
  };

  const handleIncludeCoverIntroPageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIncludeCoverIntroPage(e.target.checked);
  };

  const handleRecipientNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRecipientName(e.target.value);
  };

  const handleSenderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenderName(e.target.value);
  };

  const handleCcNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCcName(e.target.value);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleCoverDateChange = (value: string) => {
    setCoverDate(value as string);
  };
  const tableContentTooltipInfo = () => {
    return (
      <Box
        component="div"
        role="tooltip"
        data-testid="slip-sheet-table-of-contents-tooltip"
      >
        <Typography
          component="p"
          data-testid={`slip-sheet-and-cover-page-term1`}
        >
          {t('batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term1')}
        </Typography>
        <Typography
          component="p"
          data-testid={`slip-sheet-and-cover-page-term2`}
        >
          {t('batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term2')}
        </Typography>
        <Box
          component="ul"
          data-testid={`slip-sheet-and-cover-page-term3-term4`}
        >
          <li data-testid={`slip-sheet-and-cover-page-term3`}>
            {t(
              'batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term3'
            )}
          </li>
          <li data-testid={`slip-sheet-and-cover-page-term4`}>
            {t(
              'batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term4'
            )}
          </li>
        </Box>
        <Typography
          component="p"
          data-testid={`slip-sheet-and-cover-page-term5`}
        >
          {t('batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term5')}
        </Typography>
        <Typography
          component="p"
          data-testid={`slip-sheet-and-cover-page-term6`}
        >
          {t('batchPrint.slipSheetsAndCoverPage.tableOfContentsTooltip.term6')}
        </Typography>
      </Box>
    );
  };

  const slipSheetHeaderId = 'slip-sheet-header-title';
  const slipSheetDescriptionId = 'slip-sheet-description';
  const tocHeadingId = 'table-of-contents-heading';
  const tocNumberingDescriptionId = 'toc-numbering-description';
  const slipSheetsHeadingId = 'slip-sheets-heading';
  const slipSheetTypeInfoId = 'slip-sheet-type-info';
  const treeSelectorHeadingId = 'slip-sheet-selector-heading';
  const selectedFieldsGroupId = 'selected-fields-group';
  const coverPageHeadingId = 'cover-page-heading';

  return (
    <Box
      className="slip-sheet-cover-page"
      component="section"
      role="region"
      aria-labelledby={slipSheetHeaderId}
      aria-describedby={slipSheetDescriptionId}
      data-testid="slip-sheet-cover-page"
    >
      <Box
        className="slip-sheet-header"
        component="header"
        data-testid="slip-sheet-header"
      >
        <Typography
          id={slipSheetHeaderId}
          component="h2"
          className="typo-text subhead2_size"
          tabIndex={0}
          data-testid={`slip-sheet-and-cover-page-slip-sheets`}
        >
          {t('batchPrint.slipSheets')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.slipSheets')}
          dataTestId="slip-sheet-main-header-tooltip"
          trigger={
            <AccessibleTooltipTrigger
              label={t('common.infoTooltip')}
              iconClassName="info-icon"
              data-testid="main-header-info-icon"
            />
          }
          closeTooltipOnEscPress={true}
        >
          <Box
            component="div"
            role="tooltip"
            data-testid="slip-sheet-header-tooltip"
          >
            <Typography
              component="p"
              data-testid={`slip-sheet-and-cover-page-header-tooltip`}
            >
              {t('batchPrint.slipSheetsAndCoverPage.headerTooltip')}
            </Typography>
            <Typography
              component="p"
              data-testid={`slip-sheet-and-cover-page-com-tt4`}
            >
              {t('For more information visit SightlineCentral.com')}{' '}
              <a
                data-testid={`slip-sheet-and-cover-page-tt4`}
                href="https://www.sightlinecentral.com/documentation/batch-print-with-cover-and-intro-page/"
                className="slip-sheet-link-color"
                target="_blank"
                rel="noreferrer"
              >
                {t('batchPrint.toolTip.tt4')}
              </a>
            </Typography>
          </Box>
        </ArrTooltipWrapper>
      </Box>
      <Box
        className="slip-sheet-content-container"
        data-testid="slip-sheet-content-container"
      >
        <Typography
          id={slipSheetDescriptionId}
          component="p"
          className="typo-text body2_size slip-sheet-description"
          tabIndex={0}
          data-testid={`slip-sheet-and-cover-page-sub-title`}
        >
          {t('batchPrint.slipSheetsAndCoverPage.subTitle')}
        </Typography>
        <Box
          className="slip-sheet-content-section1"
          data-testid="slip-sheet-content-section1"
        >
          <Box
            data-testid="table-of-contents-section"
            className="table-of-contents-section"
            component="section"
            role="region"
            aria-labelledby={tocHeadingId}
          >
            <Box
              className="table-of-contents-toggle-row"
              data-testid="table-of-contents-toggle-row"
            >
              <FormControlLabel
                data-testid="action-label-include-table-toggle"
                control={
                  <ArrToggle
                    value={includeTableOfContent}
                    onChange={handleTableOfContentChange}
                    toggleSize="medium"
                    dataTestId="include-table-toggle"
                  />
                }
                className="switch-table-of-content"
                label={
                  <Typography
                    id={tocHeadingId}
                    component="h3"
                    className="typo-text subhead5_size"
                    tabIndex={0}
                    data-testid={`slip-sheet-and-cover-page-enable-table-of-contents-page`}
                  >
                    {t('Enable Table of Contents Page')}
                  </Typography>
                }
              />
              <ArrTooltipWrapper
                title={t('batchPrint.slipSheets')}
                dataTestId="toc-enable-page-tooltip"
                trigger={
                  <AccessibleTooltipTrigger
                    label={t('common.infoTooltip')}
                    iconClassName="info-icon"
                    data-testid="toc-info-icon"
                  />
                }
                closeTooltipOnEscPress={true}
              >
                {tableContentTooltipInfo()}
              </ArrTooltipWrapper>
            </Box>

            {includeTableOfContent && (
              <Box
                data-testid="toc-checkbox-row"
                className="toc-checkbox-row"
                ref={tocCheckboxRowRef}
                tabIndex={-1}
                role="group"
                aria-labelledby={tocNumberingDescriptionId}
                aria-live="polite"
              >
                <ArrCheckbox
                  checked={displayTOCNumberInSeq}
                  onChange={handleDisplayTOCNumberChange}
                  labelText={
                    <Typography
                      data-testid={`slip-sheet-and-cover-page-display-page-numbering-only-applies-if-you-select-one-pdf-for-all-documents-export-format`}
                      id={tocNumberingDescriptionId}
                      component="span"
                      className="typo-text body3_size"
                      tabIndex={0}
                    >
                      {t(
                        'Display Page Numbering (ONLY applies if you select "One PDF for All Documents" export format)'
                      )}
                    </Typography>
                  }
                  labelPlacement="end"
                  aria-label="Select-display-to-number-change"
                />
              </Box>
            )}
          </Box>
          <Box
            data-testid="slip-sheets-section"
            className="slip-sheets-section"
            component="section"
            role="region"
            aria-labelledby={slipSheetsHeadingId}
          >
            <Box
              data-testid="enable-slip-sheets-toggle-row"
              className={`enable-slip-sheets-toggle-row ${
                includeSlipSheet && !isDropdownEnabled ? 'no-dropdown' : ''
              }`}
            >
              <FormControlLabel
                data-testid="enable-slip-sheets-toggle-row-include-slip-toggle"
                control={
                  <ArrToggle
                    value={includeSlipSheet}
                    onChange={handleIncludeSlipSheetChange}
                    toggleSize="medium"
                    dataTestId="include-slip-toggle"
                  />
                }
                label={
                  <Typography
                    data-testid={`slip-sheet-and-cover-page-enable-slip-sheets`}
                    id={slipSheetsHeadingId}
                    component="h3"
                    className="typo-text subhead5_size"
                    tabIndex={0}
                  >
                    {t('Enable Slip Sheets')}
                  </Typography>
                }
                labelPlacement="end"
              />
            </Box>
            {includeSlipSheet && isDropdownEnabled && (
              <Box
                data-testid="slip-sheet-type-selector"
                className="slip-sheet-type-selector"
                ref={slipSheetTypeSectionRef}
                tabIndex={-1}
                aria-live="polite"
                role="group"
                aria-labelledby={slipSheetTypeInfoId}
              >
                <Box
                  className="slip-sheet-type-info"
                  data-testid="slip-sheet-type-info"
                >
                  <Typography
                    data-testid={`slip-sheet-and-cover-page-include-slip-sheets`}
                    id={slipSheetTypeInfoId}
                    component="p"
                    className="typo-text slip-sheet-info-text body3_size include-slip-sheets-text"
                    tabIndex={0}
                  >
                    {t('batchPrint.slipSheetsAndCoverPage.includeSlipSheets')}
                  </Typography>
                </Box>
                <Box
                  className="slip-sheet-type-dropdown"
                  data-testid="slip-sheet-type-dropdown"
                >
                  <ArrSingleSelectDropdown
                    dropdownLabel={t('common.select')}
                    dropdownItems={slipSheetTypeOptions}
                    selectedValues={slipSheetType}
                    onSelectionChange={handleSlipSheetTypeChange}
                    sizeVariant="medium"
                    dropdownItemKey="key"
                    dropdownItemDisplay="value"
                    disabled={!isDropdownEnabled}
                    dataTestId="asd-slipsheet-type"
                    tabIndex={-1}
                  />
                </Box>
              </Box>
            )}

            {includeSlipSheet &&
              (!isDropdownEnabled || slipSheetType.key === 'NewSlipSheets') && (
                <Box
                  className="slip-sheet-selector"
                  ref={slipSheetSelectorRef}
                  tabIndex={-1}
                  role="group"
                  aria-labelledby={treeSelectorHeadingId}
                  aria-live="polite"
                  data-testid="slip-sheet-selector"
                >
                  <Typography
                    data-testid={`slip-sheet-and-cover-page-select-fields-for-slip-sheets`}
                    id={treeSelectorHeadingId}
                    component="h4"
                    className="typo-text subhead4_size selector-title"
                    tabIndex={0}
                  >
                    {t('Select Fields for Slip Sheets')}
                  </Typography>
                  <Box
                    data-testid="workproduct-tree"
                    id={selectedFieldsGroupId}
                    className="workproduct-tree"
                    tabIndex={0}
                    role="group"
                    aria-label={t('Selected Fields')}
                  >
                    <MemoizedTreeSelector
                      restorationKey={restorationKey}
                      tabs={slipSheetTabs}
                      onSelectedItemsChange={handleSlipSheetSelection}
                    />
                  </Box>
                </Box>
              )}
          </Box>
          <Box
            data-testid="toggle-intro-page"
            className="toggle-intro-page"
            component="section"
            role="region"
            aria-labelledby={coverPageHeadingId}
          >
            <FormControlLabel
              data-testid="toggle-intro-page-include-cover-toggle"
              control={
                <ArrToggle
                  value={includeCoverIntroPage}
                  onChange={handleIncludeCoverIntroPageChange}
                  toggleSize="medium"
                  dataTestId="include-cover-toggle"
                />
              }
              label={
                <Typography
                  data-testid={`slip-sheet-and-cover-page-enable-cover-and-intro-page`}
                  id={coverPageHeadingId}
                  component="h3"
                  className="typo-text body3_size"
                  tabIndex={0}
                >
                  {t(
                    'batchPrint.slipSheetsAndCoverPage.enableCoverAndIntroPage'
                  )}
                </Typography>
              }
            />
            <ArrTooltipWrapper
              title={t(
                'batchPrint.slipSheetsAndCoverPage.enableCoverAndIntroPage'
              )}
              dataTestId="cover-intro-enable-tooltip"
              trigger={
                <AccessibleTooltipTrigger
                  label={t('common.infoTooltip')}
                  iconClassName="info-icon"
                />
              }
              closeTooltipOnEscPress={true}
            >
              <Box
                component="div"
                role="tooltip"
                data-testid="cover-intro-page-tooltip"
              >
                <Typography
                  component="p"
                  data-testid="cover-intro-page-tooltip-text"
                >
                  {t('batchPrint.slipSheetsAndCoverPage.coverAndIntroTooltip')}
                </Typography>
              </Box>
            </ArrTooltipWrapper>
          </Box>

          {includeCoverIntroPage && (
            <Box
              className="cover-page-form"
              ref={coverPageFormRef}
              tabIndex={-1}
              aria-live="polite"
              role="group"
              aria-labelledby={coverPageHeadingId}
              data-testid="cover-page-form"
            >
              <Box
                className="cover-page-section1"
                data-testid="cover-page-section1"
              >
                <ArrTextField
                  placeholder="Insert Recipient Name"
                  value={recipientName}
                  onChange={handleRecipientNameChange}
                  size="medium"
                  label="To"
                  dataTestId="enter-receipt"
                />

                <ArrTextField
                  placeholder="Insert CC Name"
                  value={ccName}
                  onChange={handleCcNameChange}
                  size="medium"
                  label="CC"
                  dataTestId="enter-cc"
                />

                <ArrTextField
                  placeholder="Insert Title"
                  value={title}
                  onChange={handleTitleChange}
                  size="medium"
                  label="Title"
                  dataTestId="enter-title"
                />
                <Box
                  data-testid="cover-date-picker-wrapper"
                  ref={coverDatePickerWrapperRef}
                  onKeyDownCapture={handleCoverDatePickerKeyDownCapture}
                >
                  <ArrDatePicker
                    type="date-picker"
                    selectedDate={[coverDate]}
                    initialStartDate={
                      coverDate === ''
                        ? formatDateCommon(new Date().toISOString())
                        : coverDate
                    }
                    onChange={(value) => handleCoverDateChange(value as string)}
                    label="Date"
                    dataTestId="slip-sheet-and-cover-page-date-picker"
                  />
                </Box>
              </Box>
              <Box
                className="cover-page-section2"
                data-testid="cover-page-section2"
              >
                <Box
                  className="form-field"
                  data-testid="cover-page-form-field-sender"
                >
                  <ArrTextField
                    placeholder="Insert Sender Name"
                    value={senderName}
                    onChange={handleSenderNameChange}
                    size="medium"
                    label="From"
                    dataTestId="enter-sender"
                  />
                </Box>
                <Box
                  className="form-field form-field-description"
                  data-testid="cover-page-form-field-description"
                >
                  <ArrTextArea
                    placeholder="Insert Description"
                    value={description}
                    onChange={handleDescriptionChange}
                    minRows={4}
                    label="Description"
                    dataTestId="insert-description"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default SlipSheetAndCoverPage;
